// hooks/useForms.js
import { useState, useEffect } from "react";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../../../firebase";
import { useUserAuth } from "../../../../context/userAuthContext";
import { FORM_TEMPLATE, FIELD_TEMPLATE } from "../config/formTypes";

export const useForms = () => {
  const [forms, setForms] = useState([]);
  const [currentForm, setCurrentForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user } = useUserAuth();

  // Generar ID único para campos temporales
  const generateId = () => {
    return Math.random().toString(36).substr(2, 9);
  };

  // Crear un nuevo formulario
  const createForm = async (formData = {}) => {
    if (!user) {
      setError("Debes estar autenticado para crear formularios");
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const newForm = {
        ...FORM_TEMPLATE,
        ...formData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: user.uid,
      };

      // Firebase genera automáticamente el ID
      const docRef = await addDoc(collection(db, "forms"), newForm);
      const formWithId = {
        ...newForm,
        id: docRef.id,
        firestoreId: docRef.id,
      };

      setForms((prev) => [formWithId, ...prev]);
      setCurrentForm(formWithId);

      return formWithId;
    } catch (err) {
      setError("Error creando formulario: " + err.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Actualizar formulario
  const updateForm = async (formId, updates) => {
    if (!user) {
      setError("Debes estar autenticado");
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      const formRef = doc(db, "forms", formId);
      const updatedData = {
        ...updates,
        updatedAt: serverTimestamp(),
      };

      await updateDoc(formRef, updatedData);

      // Actualizar estado local con fecha actual para updatedAt
      const localUpdatedData = {
        ...updates,
        updatedAt: new Date(), // Usar fecha actual para el estado local
      };

      // Actualizar estado local
      setForms((prev) =>
        prev.map((form) =>
          form.firestoreId === formId ? { ...form, ...localUpdatedData } : form
        )
      );

      if (currentForm && currentForm.firestoreId === formId) {
        setCurrentForm((prev) => ({ ...prev, ...localUpdatedData }));
      }

      return true;
    } catch (err) {
      setError("Error actualizando formulario: " + err.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Eliminar formulario
  const deleteForm = async (formId) => {
    if (!user) {
      setError("Debes estar autenticado");
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      await deleteDoc(doc(db, "forms", formId));

      setForms((prev) => prev.filter((form) => form.firestoreId !== formId));

      if (currentForm && currentForm.firestoreId === formId) {
        setCurrentForm(null);
      }

      return true;
    } catch (err) {
      setError("Error eliminando formulario: " + err.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Obtener todos los formularios del usuario
  const getUserForms = async () => {
    if (!user) {
      setError("Debes estar autenticado");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const q = query(
        collection(db, "forms"),
        where("createdBy", "==", user.uid),
        orderBy("createdAt", "desc")
      );

      const querySnapshot = await getDocs(q);
      const userForms = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        userForms.push({
          ...data,
          id: doc.id,
          firestoreId: doc.id,
          // Convertir timestamps a objetos Date para evitar errores
          createdAt: data.createdAt?.toDate
            ? data.createdAt.toDate()
            : data.createdAt,
          updatedAt: data.updatedAt?.toDate
            ? data.updatedAt.toDate()
            : data.updatedAt,
          publishedAt: data.publishedAt?.toDate
            ? data.publishedAt.toDate()
            : data.publishedAt,
          closedAt: data.closedAt?.toDate
            ? data.closedAt.toDate()
            : data.closedAt,
          archivedAt: data.archivedAt?.toDate
            ? data.archivedAt.toDate()
            : data.archivedAt,
        });
      });

      setForms(userForms);
      return userForms;
    } catch (err) {
      setError("Error obteniendo formularios: " + err.message);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Obtener un formulario específico
  const getForm = async (formId) => {
    setLoading(true);
    setError(null);

    try {
      const docRef = doc(db, "forms", formId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const formData = {
          ...data,
          id: docSnap.id,
          firestoreId: docSnap.id,
          // Convertir timestamps a objetos Date
          createdAt: data.createdAt?.toDate
            ? data.createdAt.toDate()
            : data.createdAt,
          updatedAt: data.updatedAt?.toDate
            ? data.updatedAt.toDate()
            : data.updatedAt,
          publishedAt: data.publishedAt?.toDate
            ? data.publishedAt.toDate()
            : data.publishedAt,
          closedAt: data.closedAt?.toDate
            ? data.closedAt.toDate()
            : data.closedAt,
          archivedAt: data.archivedAt?.toDate
            ? data.archivedAt.toDate()
            : data.archivedAt,
        };
        setCurrentForm(formData);
        return formData;
      } else {
        setError("Formulario no encontrado");
        return null;
      }
    } catch (err) {
      setError("Error obteniendo formulario: " + err.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Agregar campo al formulario
  const addField = (fieldData = {}) => {
    if (!currentForm) return;

    const newField = {
      ...FIELD_TEMPLATE,
      ...fieldData,
      id: generateId(),
      order: currentForm.fields.length,
    };

    const updatedForm = {
      ...currentForm,
      fields: [...currentForm.fields, newField],
    };

    setCurrentForm(updatedForm);
    return newField;
  };

  // Actualizar campo
  const updateField = (fieldId, updates) => {
    if (!currentForm) return;

    const updatedForm = {
      ...currentForm,
      fields: currentForm.fields.map((field) =>
        field.id === fieldId ? { ...field, ...updates } : field
      ),
    };

    setCurrentForm(updatedForm);
  };

  // Eliminar campo
  const removeField = (fieldId) => {
    if (!currentForm) return;

    const updatedForm = {
      ...currentForm,
      fields: currentForm.fields
        .filter((field) => field.id !== fieldId)
        .map((field, index) => ({ ...field, order: index })),
    };

    setCurrentForm(updatedForm);
  };

  // Reordenar campos
  const reorderFields = (startIndex, endIndex) => {
    if (!currentForm) return;

    const fields = Array.from(currentForm.fields);
    const [removed] = fields.splice(startIndex, 1);
    fields.splice(endIndex, 0, removed);

    const reorderedFields = fields.map((field, index) => ({
      ...field,
      order: index,
    }));

    const updatedForm = {
      ...currentForm,
      fields: reorderedFields,
    };

    setCurrentForm(updatedForm);
  };

  // Duplicar campo
  const duplicateField = (fieldId) => {
    if (!currentForm) return;

    const fieldToDuplicate = currentForm.fields.find((f) => f.id === fieldId);
    if (!fieldToDuplicate) return;

    const duplicatedField = {
      ...fieldToDuplicate,
      id: generateId(),
      title: `${fieldToDuplicate.title} (copia)`,
      order: currentForm.fields.length,
    };

    const updatedForm = {
      ...currentForm,
      fields: [...currentForm.fields, duplicatedField],
    };

    setCurrentForm(updatedForm);
    return duplicatedField;
  };

  // Validar formulario
  const validateForm = (form = currentForm) => {
    const errors = [];

    if (!form.title.trim()) {
      errors.push("El título del formulario es obligatorio");
    }

    if (form.fields.length === 0) {
      errors.push("El formulario debe tener al menos un campo");
    }

    form.fields.forEach((field, index) => {
      if (!field.title.trim()) {
        errors.push(`El campo ${index + 1} debe tener un título`);
      }

      // Validar opciones para campos que las requieren
      const requiresOptions = [
        "select",
        "radio",
        "checkbox",
        "multiple_choice",
      ];
      if (requiresOptions.includes(field.type) && field.options.length === 0) {
        errors.push(`El campo "${field.title}" debe tener al menos una opción`);
      }
    });

    return errors;
  };

  // Guardar cambios en Firebase
  const saveForm = async () => {
    if (!currentForm || !user) return false;

    const errors = validateForm();
    if (errors.length > 0) {
      setError(errors[0]);
      return false;
    }

    return await updateForm(currentForm.firestoreId, currentForm);
  };

  // Publicar formulario
  const publishForm = async (formId = currentForm?.firestoreId) => {
    if (!formId) return false;

    return await updateForm(formId, {
      status: "published",
      publishedAt: serverTimestamp(),
    });
  };

  // Despublicar formulario
  const unpublishForm = async (formId = currentForm?.firestoreId) => {
    if (!formId) return false;

    return await updateForm(formId, {
      status: "draft",
      publishedAt: null,
    });
  };

  // Cerrar formulario
  const closeForm = async (formId = currentForm?.firestoreId) => {
    if (!formId) return false;

    return await updateForm(formId, {
      status: "closed",
      closedAt: serverTimestamp(),
    });
  };

  // Archivar formulario
  const archiveForm = async (formId = currentForm?.firestoreId) => {
    if (!formId) return false;

    return await updateForm(formId, {
      status: "archived",
      archivedAt: serverTimestamp(),
    });
  };

  // Crear copia de formulario
  const duplicateForm = async (formId) => {
    if (!user) {
      setError("Debes estar autenticado");
      return null;
    }

    const formToDuplicate =
      forms.find((f) => f.firestoreId === formId) || currentForm;
    if (!formToDuplicate) {
      setError("Formulario no encontrado");
      return null;
    }

    const duplicatedForm = {
      ...formToDuplicate,
      title: `${formToDuplicate.title} (copia)`,
      status: "draft",
      responseCount: 0,
      publishedAt: null,
      closedAt: null,
      archivedAt: null,
    };

    // Remover IDs para que Firebase genere nuevos
    delete duplicatedForm.id;
    delete duplicatedForm.firestoreId;

    return await createForm(duplicatedForm);
  };

  // Obtener estadísticas del formulario
  const getFormStats = (formId = currentForm?.firestoreId) => {
    if (!formId) return null;

    const form = forms.find((f) => f.firestoreId === formId) || currentForm;
    if (!form) return null;

    const totalFields = form.fields.length;
    const requiredFields = form.fields.filter((f) => f.required).length;
    const optionalFields = totalFields - requiredFields;

    const fieldTypes = form.fields.reduce((acc, field) => {
      acc[field.type] = (acc[field.type] || 0) + 1;
      return acc;
    }, {});

    const estimatedTime = form.fields.reduce((time, field) => {
      const timeMap = {
        text: 10,
        textarea: 30,
        email: 8,
        number: 5,
        phone: 8,
        date: 5,
        time: 5,
        select: 5,
        radio: 8,
        checkbox: 10,
        yes_no: 3,
        rating: 5,
        scale: 5,
        file: 60,
        url: 15,
      };
      return time + (timeMap[field.type] || 10);
    }, 0);

    return {
      totalFields,
      requiredFields,
      optionalFields,
      fieldTypes,
      estimatedTime: Math.ceil(estimatedTime / 60), // en minutos
      responseCount: form.responseCount || 0,
      status: form.status,
      isPublished: form.status === "published",
      createdAt: form.createdAt,
      lastUpdated: form.updatedAt,
    };
  };

  // Generar URL pública del formulario
  const getPublicFormUrl = (formId) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/form/${formId}`;
  };

  // Generar código para embeber
  const getEmbedCode = (formId, width = "100%", height = "600") => {
    const url = getPublicFormUrl(formId);
    return `<iframe src="${url}" width="${width}" height="${height}" frameborder="0" style="border: none;"></iframe>`;
  };

  // Buscar formularios
  const searchForms = async (searchTerm) => {
    if (!user) return [];

    try {
      const q = query(
        collection(db, "forms"),
        where("createdBy", "==", user.uid),
        orderBy("createdAt", "desc")
      );

      const querySnapshot = await getDocs(q);
      const allForms = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        allForms.push({
          ...data,
          id: doc.id,
          firestoreId: doc.id,
          // Convertir timestamps
          createdAt: data.createdAt?.toDate
            ? data.createdAt.toDate()
            : data.createdAt,
          updatedAt: data.updatedAt?.toDate
            ? data.updatedAt.toDate()
            : data.updatedAt,
          publishedAt: data.publishedAt?.toDate
            ? data.publishedAt.toDate()
            : data.publishedAt,
          closedAt: data.closedAt?.toDate
            ? data.closedAt.toDate()
            : data.closedAt,
          archivedAt: data.archivedAt?.toDate
            ? data.archivedAt.toDate()
            : data.archivedAt,
        });
      });

      // Filtrar localmente por título y descripción
      const filtered = allForms.filter(
        (form) =>
          form.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (form.description &&
            form.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );

      return filtered;
    } catch (err) {
      setError("Error buscando formularios: " + err.message);
      return [];
    }
  };

  // Filtrar formularios por estado
  const getFormsByStatus = (status) => {
    return forms.filter((form) => form.status === status);
  };

  // Obtener formularios recientes
  const getRecentForms = (limit = 5) => {
    return forms.slice(0, limit);
  };

  // Limpiar estados
  const clearError = () => setError(null);
  const clearCurrentForm = () => setCurrentForm(null);

  // Resetear hook
  const reset = () => {
    setForms([]);
    setCurrentForm(null);
    setLoading(false);
    setError(null);
  };

  return {
    // Estados
    forms,
    currentForm,
    loading,
    error,

    // Funciones de formularios
    createForm,
    updateForm,
    deleteForm,
    getUserForms,
    getForm,
    saveForm,
    publishForm,
    unpublishForm,
    closeForm,
    archiveForm,
    duplicateForm,

    // Funciones de campos
    addField,
    updateField,
    removeField,
    reorderFields,
    duplicateField,

    // Funciones de búsqueda y filtrado
    searchForms,
    getFormsByStatus,
    getRecentForms,

    // Utilidades
    validateForm,
    getFormStats,
    getPublicFormUrl,
    getEmbedCode,
    clearError,
    clearCurrentForm,
    setCurrentForm,
    reset,
  };
};

export default useForms;
